import { useRef } from 'react';
import clsx from 'clsx';
import styles from './link.module.css';
import { Portal, useReducedMotion } from '@nula/core-ui';
import { useEntityPreview } from '@/hooks/use-entity-preview';
import { extractDocumentId } from '@/lib/documents/extract-document-id';
import { EntityPreviewCard } from '@/components/entity-preview-card/entity-preview-card';
import { useNavigateToEntity } from '@/hooks/navigation';

interface DocumentLinkProps {
  href?: string;
  children?: React.ReactNode;
  /** Override click handler — used for collection links that need custom navigation. */
  onNavigate?: () => void;
  className?: string;
}

/**
 * Renders a styled link. For `nula://document/` or bare UUID links, shows a
 * hover preview card to the right of the link, vertically centered.
 * External links open in a new tab. Collection/other links can pass
 * `onNavigate` to get the same appearance without a preview card.
 */
export function Link({ href, children, onNavigate, className }: DocumentLinkProps) {
  const documentId = href ? extractDocumentId(href) : null;
  const linkRef = useRef<HTMLAnchorElement>(null);
  const navigateToEntity = useNavigateToEntity();

  const {
    onMouseEnter,
    onMouseLeave,
    onCardMouseEnter,
    onCardMouseLeave,
    previewData,
    isOpen,
    loading,
  } = useEntityPreview(documentId ?? '');

  const shouldReduce = useReducedMotion();

  const handleOpen = (id: string) => {
    navigateToEntity(id);
  };

  // External link — render normally, no preview
  if (!documentId && !onNavigate) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={clsx(styles.link, className)}
      >
        {children}
      </a>
    );
  }

  // Collection/override link — same appearance as document link, no preview card
  if (!documentId && onNavigate) {
    return (
      <a
        href={href}
        onClick={(e) => { e.preventDefault(); onNavigate(); }}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onNavigate(); } }}
        className={clsx(styles.link, className)}
      >
        {children}
      </a>
    );
  }

  const getCardStyle = (): React.CSSProperties => {
    if (!linkRef.current) return { display: 'none' };
    const rect = linkRef.current.getBoundingClientRect();
    return {
      '--preview-top': `${rect.top + rect.height / 2}px`,
      '--preview-left': `${rect.left - 8}px`,
    } as React.CSSProperties;
  };

  return (
    <>
      <a
        ref={linkRef}
        href={href}
        onClick={(e) => { e.preventDefault(); handleOpen(documentId!); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleOpen(documentId!);
          }
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className={clsx(styles.link, className)}
      >
        {children}
      </a>
      {isOpen && (
        <Portal>
          <div
            role="tooltip"
            style={getCardStyle()}
            className={clsx(styles.previewCard, shouldReduce && styles.previewCardReduced)}
            onMouseEnter={onCardMouseEnter}
            onMouseLeave={onCardMouseLeave}
          >
            <EntityPreviewCard data={previewData} loading={loading} onOpen={handleOpen} />
          </div>
        </Portal>
      )}
    </>
  );
}
